import { Box, Kbd, LoadingOverlay, Space, Text, Title } from '@mantine/core';
import { createSpotlight, Spotlight } from '@mantine/spotlight';
import { tKeys, useTranslation } from 'locales';
import { useGetShortcutKeys } from 'modules/Settings/Profile';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { isNullish } from 'remeda';
import { useLoadLightDebtors } from 'shared/hooks/use-load-light-debtors';
import { useSearchInvoices } from 'shared/utils/hooks';
import type { LightInvoice } from 'types/store/invoice-state';

import { spotlightStylesOverride } from './NavigationSpotlight';

const [store, spotlightControls] = createSpotlight();
const openSearchSpotlight = spotlightControls.open;

export { openSearchSpotlight };

export const SearchSpotlight = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [invoices, setInvoices] = useState<Array<LightInvoice>>();
  const { getInvoices } = useSearchInvoices();
  const { lightDebtors, isLightDebtorsLoading } = useLoadLightDebtors({
    query: query.startsWith('@') ? query.slice(1) : '',
  });

  const { search: shortcutKey } = useGetShortcutKeys();

  useEffect(() => {
    if (query.startsWith('#'))
      getInvoices({ reference: query.slice(1) }, ({ data }) => setInvoices(data));
  }, [query]);

  const actions = (() => {
    if (query.startsWith('@')) {
      return (lightDebtors ?? []).map(({ id, fullName }) => ({
        id: String(id),
        label: fullName,
        onClick: () => history.push(`/clients/${id}`),
      }));
    }

    if (query.startsWith('#')) {
      return (invoices ?? []).map(invoice => ({
        id: String(invoice.id),
        // @ts-expect-error attributes is poorly typed
        label: invoice.attributes.reference,
        onClick: () => history.push(`/invoices/${invoice.id}`),
      }));
    }

    return [];
  })();

  const isLoading = isLightDebtorsLoading || (query.includes('#') && isNullish(invoices));

  return (
    <Spotlight
      store={store}
      actions={actions}
      shortcut={shortcutKey}
      centered
      limit={10}
      highlightQuery
      radius={10}
      filter={(_, actionsArray) => actionsArray}
      query={query}
      classNames={{
        action: spotlightStylesOverride.action,
      }}
      onQueryChange={searchInput => {
        if (searchInput === shortcutKey) spotlightControls.close();
        setQuery(searchInput);
      }}
      nothingFound={
        <Box>
          <LoadingOverlay visible={isLoading} />
          <Title order={3} mb="md">
            {t(tKeys.SPOTLIGHT.NO_RESULTS)}
          </Title>
          <Text>
            {t(tKeys.SPOTLIGHT.TYPE)} <Kbd>@</Kbd> {t(tKeys.SPOTLIGHT.FOR_CLIENTS)}
            <Space h="xs" />
            {t(tKeys.SPOTLIGHT.TYPE)} <Kbd>#</Kbd> {t(tKeys.SPOTLIGHT.FOR_INVOICES)}
          </Text>
        </Box>
      }
    />
  );
};
